import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '../primitives/Button'
import { AlertPanel } from '../primitives/Panel'
import { canGoStale, type Surface } from './surfaces'
import '../notes.css'

/**
 * The five states every list, chart and panel routes through.
 *
 * A discriminated union rather than optional-field soup, so `data` is only
 * reachable in the two states that have it and the render prop cannot be
 * invoked with undefined.
 *
 * **Stale is not an error.** It shows the real data with the date it is from.
 * Per D20 stale is bank-sync freshness reported by the server — the web client
 * is online-only and has no cache to age — so surfaces with no upstream sync
 * (categories, budgets, rules, reports) never enter it and genuinely have four
 * states. A panel totalling across accounts sets `asOf` from the OLDEST
 * contributing `last_synced_at`, since a total is only as current as its
 * stalest input.
 */
export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'stale'; data: T; asOf: Date }
  | { status: 'error'; message: string; recovery?: { label: string; onAct: () => void } }
  | { status: 'success'; data: T }

type Props<T> = {
  state: AsyncState<T>
  /**
   * Which surface this is — issues 85 and 251, settled 2026-08-13.
   *
   * **Required, and that was the open question.** Plan Task 13 contradicted
   * itself three ways: its Interfaces block declared `onRetry`, its design-rules
   * paragraph declared a required `surface` and an optional `asOf`, and the
   * implementation shipped none of the three. #85 declined to pick, correctly —
   * "picking one reading would set an API that every surface in Phases 3–8
   * depends on" — and it stayed open until there was evidence rather than a
   * preference.
   *
   * The evidence is the twelve-module treatment matrix recovered from
   * `TrackE5 Web States.dc.html` and transcribed into spec §0.5.1. That table
   * is keyed by screen: it is a surface → treatment lookup, which is the same
   * shape as this prop. Two requirements were already written as though it
   * existed — M0-R9's "the surface registry records which kind each surface is"
   * and spec §Analytics' `stale_state_shown` "with `surface` taken from a
   * required prop".
   *
   * `onRetry` and `asOf` stay ABSENT, and #85's other two readings are answered
   * rather than deferred: `recovery: { label, onAct }` is strictly more
   * expressive than `onRetry` because it also names the action — two live
   * callers use different labels, 'Try again' and 'Reload the page', which one
   * callback could not have produced. And `asOf` lives inside the `stale`
   * variant, computed by `fromQuery` from the DATA rather than from cache age;
   * a prop would be a second source that drifts.
   *
   * Taken now because it costs THREE call sites today and every surface in
   * Phases 3–8 later.
   */
  surface: Surface
  skeleton: ReactNode
  empty: {
    title: string
    /** Optional, and separate from `title` — a caller with more to explain
     *  than a heading should carry (an invitations panel's "what does
     *  inviting someone do", say) gets a real title plus its own body text,
     *  rather than one long sentence rendered at heading weight. */
    body?: string
    action?: { label: string; onAct: () => void }
    /**
     * A mark ahead of the title, for an empty that is a SUCCESS ("Nothing to review") rather than an
     * absence. Decorative: the title already says it, so it is hidden from assistive technology
     * (colour and shape are never the only signal). Carries no colour of its own: the consumer
     * styles what it passes, in CSS.
     */
    icon?: ReactNode
  }
  children: (data: T) => ReactNode
  /**
   * What to say when the wait ends — issue 97.
   *
   * **Opt-in, and that is the whole design.** Before this, the region said
   * "Loading" and then went silent: a screen-reader user heard the wait start
   * and never heard it end, while a sighted user got the answer for free. The
   * cheap fix — announcing "Loaded" from every surface — is worse than the
   * silence it replaces once a page carries several of these and each one
   * speaks on every refetch. So a surface says something only if it has
   * something worth saying, the way `empty.title` is already supplied rather
   * than derived.
   *
   * Called on the `loading` → `success`/`stale` edge only, with the resolved
   * data, so the text can carry a count. `empty` and `error` are deliberately
   * not announced here: empty renders its title as visible text, and error
   * already carries its own `role="alert"`.
   */
  announce?: (data: T) => string
  /**
   * The stale banner's own sentence — M5 Ticket 1 (#425), architect review.
   *
   * **Optional, defaulting to today's "Showing data from {date}"**, so every
   * existing call site is unchanged. Added because two `<Async>`s can now sit
   * on ONE page (`AccountsSummary.tsx`'s Connections tile nests its own
   * `surface="aggregator-items"` alongside the page's own `surface="accounts"`
   * — `AccountDetail.tsx`'s `OwnerMenu` is the same shape, and exactly what
   * rule 10 asks for) and, for
   * a single-institution household, both surfaces' `asOf` trace to the SAME
   * `last_synced_at` — so without this they rendered the byte-identical
   * sentence twice on one page, which `account-detail.spec.ts`'s own
   * `getByText('Showing data from …')` caught resolving to two elements.
   *
   * A caller nesting a second `<Async>` supplies a sentence naming ITS OWN
   * subject instead — Connections' own copy is "Connection status from …",
   * distinct from the page's account data — rather than the two surfaces
   * disagreeing about which fact is stale while saying the identical words.
   */
  staleLabel?: (asOf: Date) => string
  /**
   * A class for the `aria-busy` wrapper every state renders inside (see the comment on it below). That
   * wrapper is a real box, so in a flex or grid parent IT is the item: a caller whose content must
   * shrink or fill (a sheet's scrolling body) styles it here. Absent, the wrapper has no class.
   */
  className?: string
}

/**
 * Hard-coded to en-GB, matching the plan. Every other date in the app will
 * want the user's locale, so this wants revisiting when a locale source
 * exists — tracked rather than guessed at.
 *
 * Formatted in the viewer's timezone deliberately: `asOf` is an instant, and
 * "showing data from" means the date it was on where the reader is.
 */
const DATE = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

/** The default `staleLabel` — every call site before this prop existed. */
function defaultStaleLabel(asOf: Date): string {
  return `Showing data from ${DATE.format(asOf)}`
}

/** Off-screen but reachable by assistive technology. Inline rather than a
 *  utility class so this component needs no stylesheet of its own. */
const OFFSCREEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
} as const

/** The five state branches. The live region lives in `Async` below, not here —
 *  it has to sit outside this switch to keep a stable position. */
function AsyncBody<T>({
  state,
  surface,
  skeleton,
  empty,
  children,
  staleLabel = defaultStaleLabel,
}: Props<T>) {
  /**
   * The four-versus-five rule, enforced rather than documented — issue 251.
   *
   * M0-R9 says a surface with no upstream sync implements four states and
   * "stale is not available to them". Nothing checked that. `fromQuery` makes
   * it *structurally* unlikely — stale requires an `asOf` and a live surface
   * configures none — but "unlikely by construction" is what this repo calls a
   * shape guard, and shape guards lose to outcome guards. A caller hand-rolling
   * an `AsyncState` (three of them do, and `ErrorBoundary` is one) bypasses
   * `fromQuery` entirely.
   *
   * Throws rather than falling back to `success`, because a live surface
   * claiming a sync date is not a rendering problem to paper over — it means
   * the data layer has invented a freshness it cannot have, and rendering it
   * would put a wrong date in front of someone's money. `ErrorBoundary` is
   * upstream of every route, so this surfaces as a caught error rather than a
   * white screen.
   */
  if (state.status === 'stale' && !canGoStale(surface)) {
    throw new Error(
      `Async: surface "${surface}" is live and cannot be stale. ` +
        `Stale is bank-sync freshness (D20); a surface with no upstream sync has ` +
        `no last_synced_at to be behind. Either it is synced — say so in SURFACES — ` +
        `or the state is wrong.`,
    )
  }

  if (state.status === 'loading') return <>{skeleton}</>

  if (state.status === 'empty') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: 20 }}>
        {empty.icon !== undefined && (
          <span aria-hidden="true" style={{ display: 'inline-flex' }}>
            {empty.icon}
          </span>
        )}
        <span style={{ fontSize: 'var(--type-row-strong)', fontWeight: 500, color: 'var(--ink)' }}>{empty.title}</span>
        {/* `.note` — the class `notes.css` exists for, not a third inline
            `color: var(--ink2)` alongside the two that were already moved out
            of this file (`notes.test.ts`'s "no other component still inlines
            the note pattern" is a repo-wide sweep, not scoped to the two
            files that prompted it). */}
        {empty.body && <span className="note">{empty.body}</span>}
        {empty.action && (
          <span>
            <Button size="panel" onClick={empty.action.onAct}>
              {empty.action.label}
            </Button>
          </span>
        )}
      </div>
    )
  }

  if (state.status === 'error') {
    return (
      <AlertPanel style={{ gap: 9 }}>
        {/* role="alert" on the inner node rather than the panel: the panel is
            a layout box, and an assertive region should wrap the message. */}
        <span role="alert" style={{ fontSize: 'var(--type-row-strong)' }}>
          {state.message}
        </span>
        {state.recovery && (
          <span>
            <Button size="panel" variant="outline" onClick={state.recovery.onAct}>
              {state.recovery.label}
            </Button>
          </span>
        )}
      </AlertPanel>
    )
  }

  if (state.status === 'stale') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }} data-async="stale">
        {/* Not role="alert" — stale is a note about freshness, not a failure,
            and the data below it is real. */}
        <span className="note note--stale">{staleLabel(state.asOf)}</span>
        {children(state.data)}
      </div>
    )
  }

  return <>{children(state.data)}</>
}

export function Async<T>(props: Props<T>) {
  const { state, announce } = props
  const [announcement, setAnnouncement] = useState('')
  /**
   * The status at the last transition, so the effect can tell an EDGE from a
   * render. Seeded with the first status rather than with `'loading'`: a
   * surface mounted straight into `success` never waited, so there is nothing
   * to report the end of.
   */
  const previous = useRef(state.status)

  /**
   * In an effect rather than derived during render, because a ref written
   * during render is impure — StrictMode's double invocation would consume the
   * edge on the discarded pass and the announcement would never arrive.
   *
   * `state` is in the deps and changes identity on most renders; the ref guard
   * is what makes that harmless. It also gives the other half for free: a
   * resolved → resolved data change is not an edge, so a refetch that never
   * passes through `loading` announces nothing.
   */
  useEffect(() => {
    const was = previous.current
    if (was === state.status) return
    previous.current = state.status
    // Cleared on every other transition, and the clear is load-bearing. React
    // writing the SAME string to the SAME text node is not a mutation and
    // announces nothing, so without passing through '' a surface that resolves
    // to identical text twice is silent the second time — the defect
    // `useAnnouncement` exists for, reached by a different route.
    setAnnouncement(
      was === 'loading' && (state.status === 'success' || state.status === 'stale')
        ? (announce?.(state.data) ?? '')
        : '',
    )
  }, [state, announce])

  return (
    <>
      {/* One node, one position, every state. React reconciles fragment
          children positionally, so this span is never unmounted and only its
          text changes — which is what a screen reader actually announces.
          Rendering it inside each branch looked equivalent and was not: the
          branches return different container elements, so React tore the
          region down and rebuilt it on every transition, announcing no more
          reliably than a fresh insertion. A test pins the node identity. */}
      <span role="status" aria-live="polite" style={OFFSCREEN}>
        {state.status === 'loading' ? 'Loading' : announcement}
      </span>
      {/**
       * The loading region, and the element exists only to carry `aria-busy`
       * — issue 264.
       *
       * The Design System's §Loading & progress asks for three things
       * together: *"Loading regions carry `aria-busy="true"` and a polite
       * live-region message; `prefers-reduced-motion` freezes every animation
       * to its resting frame."* Two shipped and this one did not, because
       * there was no element to put it on — the fragment above is deliberate,
       * since the live region has to hold a stable DOM position or it stops
       * announcing.
       *
       * The other three placements were each measured against Chrome's
       * accessibility tree rather than reasoned about, and all three are
       * wrong:
       *
       *   - **On the live region.** `aria-busy` tells AT to SUPPRESS updates
       *     until it clears, which would silence the "Loading" message the
       *     same requirement pairs it with.
       *   - **On the skeleton.** `Skeleton` is `aria-hidden`, and the probe is
       *     unambiguous: that node is not in the accessibility tree at all, so
       *     the attribute reaches nobody. An assertion that `aria-busy` exists
       *     somewhere in the DOM passes for this — the proxy test CLAUDE.md
       *     names.
       *   - **The caller's job**, by documented contract. No structure change,
       *     and it relies on every future surface remembering — which is
       *     exactly what `surfaces.ts` exists to stop for the sibling problem.
       *
       * So: a wrapper. A plain `<div>` measures `role=generic ignored=false
       * busy=1`, so it needs no role to be reached, and `aria-busy="false"`
       * emits no busy property at all — the clear is a real clear rather than
       * a lingering "not busy" node.
       *
       * **No `display: contents`.** It measures identically in this engine,
       * and that is the trap: the property has a history of removing elements
       * from the accessibility tree, which is precisely the failure being
       * fixed. A real box that costs one flex/grid nesting level is cheaper
       * than a correctness argument that depends on a browser version. What it
       * costs was checked at 320px rather than assumed.
       */}
      <div aria-busy={state.status === 'loading'} className={props.className}>
        <AsyncBody {...props} />
      </div>
    </>
  )
}
